import {
  IExpectedHoursItem,
  IWorktimePayoutBreakdownLine,
  IYouTrackProjectHoursLine,
  type IExpectedHoursListFilters,
} from "./types/expectedHours";
import appErrorToast from "../components/common/AppErrorToast";
import dataTimeService from "./dataTime.service";
import axiosInstance from "../utils/axios";
import moment from "moment";
import { WorkTimeSettlement } from "./types/worktimeSettlement";
import apiService from "./api.service";
import { downloadExcelBlobFromUrl } from "./excelBlobDownload.service";

const pathPrefix = `${process.env.REACT_APP_SERVER_URL}/api`;

export interface IWorkItemsFilters {
  employeeId?: string;
  contractType?: "B2B" | "EmploymentContract" | "ContractOfMandate" | "Intern";
  onlyWithoutContractType?: boolean;
  settlementStatus?: "Pending" | "Rejected" | "Accepted";
  onlyWithoutSettlementDecision?: boolean;
}

export function buildWorkItemsApiFilters(
  filters?: IExpectedHoursListFilters
): IWorkItemsFilters {
  const apiFilters: IWorkItemsFilters = {};
  if (!filters) return apiFilters;
  if (filters.employeeId) apiFilters.employeeId = filters.employeeId;
  if (filters.contractType === "NONE") apiFilters.onlyWithoutContractType = true;
  else if (filters.contractType) apiFilters.contractType = filters.contractType;
  if (filters.settlementStatus === "NONE") apiFilters.onlyWithoutSettlementDecision = true;
  else if (filters.settlementStatus) apiFilters.settlementStatus = filters.settlementStatus;
  return apiFilters;
}

function applyWorkItemsFiltersToUrl(baseUrl: string, filters?: IWorkItemsFilters): string {
  const params: { key: string; value: string }[] = [];

  if (filters?.employeeId) params.push({ key: "employeeId", value: filters.employeeId });
  if (filters?.contractType) params.push({ key: "contractType", value: filters.contractType });
  if (filters?.onlyWithoutContractType) params.push({ key: "onlyWithoutContractType", value: "true" });
  if (filters?.settlementStatus) params.push({ key: "settlementStatus", value: filters.settlementStatus });
  if (filters?.onlyWithoutSettlementDecision) params.push({ key: "onlyWithoutSettlementDecision", value: "true" });

  if (params.length === 0) return baseUrl;
  return apiService.setPathParams(baseUrl, params);
}

async function getWorkMinutesByUserForMonth(
  month: number,
  year: number,
  filters?: IWorkItemsFilters
): Promise<WorkTimeSettlement[]> {
  const url = applyWorkItemsFiltersToUrl(
    `${pathPrefix}/workItems?year=${year}&month=${month}`,
    filters
  );

  const response = await axiosInstance.get<WorkTimeSettlement[]>(url);
  return response.data;
}

export interface ISettlementActionPayload {
  userId: string;
  year: number;
  month: number;
}

export interface IRejectSettlementActionPayload extends ISettlementActionPayload {
  rejectionReason: string;
}

export async function getExpectedHoursForMonth(
  year: number,
  month: number,
  filters?: IWorkItemsFilters
): Promise<IExpectedHoursItem[]> {
  try {
    const settlements = await getWorkMinutesByUserForMonth(month, year, filters);

    return settlements.map((x) => {
      const absentify = x.absentifyInMinutes ?? 0;
      const collected = x.collectedTimeInMinutes ?? 0;
      const workingHours = x.workingHoursInMinutes ?? 0;
      const toCollect = x.timeToCollectInMinutes ?? 0;

      return {
        id: `${x.userId}-${year}-${month}`,
        userId: x.userId,
        year,
        month,
        userName: `${x.lastName} ${x.firstName}`,
        contractType: x.contractType,
        hasIncompleteWorkHours: x.hasIncompleteWorkHours,
        hasWorkTimeWithoutOrders: x.hasWorkTimeWithoutOrders,
        resourceAllocationSettlementStatus: x.resourceAllocationSettlementStatus,
        canAcceptMainSettlement: x.canAcceptMainSettlement,
        youtrack: dataTimeService.convertMinutesToHours(x.workTimeInMinutes),
        absentify: dataTimeService.convertMinutesToHours(absentify),
        toCollect: dataTimeService.convertMinutesToHours(toCollect),
        collected: dataTimeService.convertMinutesToHours(collected),
        realizedToCollectOvertime: dataTimeService.convertMinutesToHours(
          x.realizedToCollectionOvertimeInMinutes ?? 0
        ),
        timePaidout100: dataTimeService.convertMinutesToHours(x.timeToPay100InMinutes ?? 0),
        timePaidout150: dataTimeService.convertMinutesToHours(x.timeToPay150InMinutes ?? 0),
        timePaidout200: dataTimeService.convertMinutesToHours(x.timeToPay200InMinutes ?? 0),
        workingHours: dataTimeService.convertMinutesToHours(workingHours),
        total: x.totalInHours,
        target: x.targetInHours,
        acceptedTotal: x.acceptedTotalInHours ?? null,
        settlementStatus: x.settlementStatus,
        rejectionReason: x.rejectionReason ?? "",
        decisionAt: x.decisionAt,
        decisionByUserId: x.decisionByUserId,
        isValuesConsistent: x.isValuesConsistent,
        baselineDataChangedAfterDecision: x.baselineDataChangedAfterDecision,
        baselineDataChangedAtUtc: x.baselineDataChangedAtUtc ?? null,
        baselineDataChangedByUserId: x.baselineDataChangedByUserId ?? null,
        baselineDataChangeReason: x.baselineDataChangeReason ?? null,
      }
    })
  } catch (error) {
    appErrorToast(`Błąd w pobieraniu danych rozliczenia: ${error}`);
    return [];
  }
}

export async function acceptSettlement(payload: ISettlementActionPayload): Promise<void> {
  try {
    const url = `${pathPrefix}/workItems/${payload.userId}/settlement/accept?year=${payload.year}&month=${payload.month}`;
    await axiosInstance.put(url);
  } catch (error) {
    appErrorToast(`Błąd podczas akceptacji rozliczenia: ${error}`);
    throw error;
  }
}

export async function rejectSettlement(payload: IRejectSettlementActionPayload): Promise<void> {
  try {
    const url = `${pathPrefix}/workItems/${payload.userId}/settlement/reject?year=${payload.year}&month=${payload.month}`;
    await axiosInstance.put(url, { rejectionReason: payload.rejectionReason });
  } catch (error) {
    appErrorToast(`Błąd podczas odrzucania rozliczenia: ${error}`);
    throw error;
  }
}

export async function getPayoutBreakdown(
  userId: string,
  year: number,
  month: number,
  bucket: 100 | 150 | 200
): Promise<IWorktimePayoutBreakdownLine[]> {
  const url = `${pathPrefix}/workItems/${userId}/payout-breakdown?year=${year}&month=${month}&bucket=${bucket}`;
  const response = await axiosInstance.get<IWorktimePayoutBreakdownLine[]>(url);
  return response.data ?? [];
}

export async function getYouTrackBreakdown(
  userId: string,
  year: number,
  month: number
): Promise<IYouTrackProjectHoursLine[]> {
  const url = `${pathPrefix}/workItems/${userId}/youtrack-breakdown?year=${year}&month=${month}`;
  const response = await axiosInstance.get<IYouTrackProjectHoursLine[]>(url);
  return response.data ?? [];
}

function settlementExportFallbackFilename(year: number, month: number): string {
  return `RozliczenieGodzinUslug_${year}_${String(month).padStart(2, "0")}_${moment().format("DDMMYYYYHHmmss")}.xlsx`;
}

const settlementExportErrorMessage =
  "Nie udało się wygenerować eksportu rozliczenia godzin usług.";

export function exportWorkItemsXlsx(
  year: number,
  month: number,
  filters: IExpectedHoursListFilters | undefined,
  setIsExporting: (value: boolean) => void
): void {
  const apiFilters = buildWorkItemsApiFilters(filters);
  const url = applyWorkItemsFiltersToUrl(
    `${pathPrefix}/workItems/export/xlsx?year=${year}&month=${month}`,
    apiFilters
  );
  void downloadExcelBlobFromUrl({
    url,
    fallbackDownloadName: settlementExportFallbackFilename(year, month),
    setIsLoading: setIsExporting,
    genericErrorMessage: settlementExportErrorMessage,
  });
}